import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState, useEffect } from "react";
import useStore from "../../../helpers/useStore";
import { DoctorContext } from "../../../stores/Doctor/doctor.provider";
import {
  ClinicForModal,
  ClinicToUpdate,
  DoctorToUpdate,
} from "../DoctorTabContent/doctor-client-types";
import LoadingSpinner from "../../BIDashboard/components/LoadingSpinner";
import { UpdateClinicDto } from "../../../api-client";
import {
  DeleteDoctorDialog,
  ErrorDialog,
  EditDoctorDialog,
  EditClinicDialog,
  DeleteClinicDialog,
} from "./DoctorClinicAuxDialoges";
import ClinicInfoDisplay from "./ClinicInfoDisplay";
import ClinicInfoEditForm from "./ClinicInfoEditForm";
import ClinicTicketsSection from "./ClinicTicketsSection";
import ClinicDrReportsSection from "./ClinicDrReportsSection";
import { useCreateDoctorWithClinic, useRemoveDoctor, useUpdateDoctor } from "../../../stores/Doctor/doctor.mutations";
import MergeEntityDialog from "../MergeDialog/MergeEntityDialog";

interface DoctorClinicDialogProps {
  openAddEditDialog: boolean;
  handleCloseAddEditDialog: () => void;
  onDataSaved: () => void;
  clinicId?: string;
}

const DoctorClinicDialog: React.FC<DoctorClinicDialogProps> = ({
  openAddEditDialog,
  handleCloseAddEditDialog,
  onDataSaved,
  clinicId = undefined,
}) => {
  const DoctorStore = useStore(DoctorContext);
  const {
    getClinicById,
    getDoctorById,
    removeDoctorFromClinic,
    updateClinic,
    removeClinic,
  } = DoctorStore;

  const initialClinicForm = {
    id: "",
    BSNR: "",
    businessName: "",
    phone1: "",
    fax: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    zip: "",
    email: "",
    notes: "",
    methodForDrReport: "",
    methodForRxOrder: "",
  };

  const initialDoctorForms = [
    {
      id: "",
      LANR: "",
      salutation: "",
      firstNames: "",
      lastName: "",
    },
  ];

  enum ReportMethod {
    Email = "Email",
    Fax = "Fax",
    Post = "Post",
  }

  const [clinicForm, setClinicForm] = useState(initialClinicForm);
  const [doctorForms, setDoctorForms] = useState(initialDoctorForms);
  const [newDoctorForm, setNewDoctorForm] = useState<{
    id: string;
    LANR: string;
    salutation: string;
    firstNames: string;
    lastName: string;
  } | null>(null);
  const [isNewDoctor, setIsNewDoctor] = useState(false);
  const [isClinicValid, setIsClinicValid] = useState(false);
  const [isDoctorValid, setIsDoctorValid] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState<any>(null);
  const [deleteAction, setDeleteAction] = useState<"delete" | "removeFromClinic">("delete");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [doctorToEdit, setDoctorToEdit] = useState<any>(null);
  const [editedDoctorId, setEditedDoctorId] = useState<string>("");
  const [openEditClinicDialog, setOpenEditClinicDialog] = useState<boolean>(false);
  const [clinicToEdit, setClinicToEdit] = useState<any>(null);
  const [openDeleteClinicDialog, setOpenDeleteClinicDialog] = useState<boolean>(false);
  const [clinicToDelete, setClinicToDelete] = useState<any>(null);
  const [kebabAnchorEl, setKebabAnchorEl] = useState<null | HTMLElement>(null);
  const [kebabDoctor, setKebabDoctor] = useState<any>(null);
  const [mergeDoctorId, setMergeDoctorId] = useState<string | null>(null);
  const [mergeDoctorOpen, setMergeDoctorOpen] = useState(false);
  const [clinicKebabAnchorEl, setClinicKebabAnchorEl] = useState<null | HTMLElement>(null);
  const [mergeClinicOpen, setMergeClinicOpen] = useState(false);

  const removeDoctorMutation = useRemoveDoctor();
  const updateDoctorMutation = useUpdateDoctor();
  const createDoctorWithClinicMutation = useCreateDoctorWithClinic();

  const handleSave = async () => {
    // Only create a new doctor if newDoctorForm is filled (id is not empty)
    if (!newDoctorForm || !newDoctorForm.id) {
      // Optionally show an error or just return
      setErrorMessage("Bitte geben Sie die Daten für den neuen Arzt ein.");
      setOpenErrorDialog(true);
      return;
    }

    // LANR must be either empty or exactly 9 characters.
    // Skip this check if the doctor already exists (loaded from backend) — in that
    // case newDoctorForm.id holds the existing LANR or other identifier, not a value
    // the user is trying to register.
    if (!isDoctorValid) {
      const lanrLen = (newDoctorForm.id || "").trim().length;
      if (lanrLen !== 0 && lanrLen !== 9) {
        setErrorMessage("LANR muss entweder leer gelassen (wird automatisch generiert) oder genau 9 Zeichen lang sein.");
        setOpenErrorDialog(true);
        return;
      }
    }

    // BSNR must be either empty or exactly 9 characters.
    // Skip this check if the clinic already exists (loaded from backend) — in that
    // case clinicForm.id holds the clinic's UUID, not a BSNR the user typed.
    if (!isClinicValid) {
      const bsnrLen = (clinicForm.id || "").trim().length;
      if (bsnrLen !== 0 && bsnrLen !== 9) {
        setErrorMessage("BSNR muss entweder leer gelassen (wird automatisch generiert) oder genau 9 Zeichen lang sein.");
        setOpenErrorDialog(true);
        return;
      }
    }

    const createDoctorClinicDto = {
      id: newDoctorForm.id,
      LANR: newDoctorForm.id || undefined,
      contactInfo: {
        salutation: newDoctorForm.salutation || "",
        firstNames: newDoctorForm.firstNames || "",
        lastName: newDoctorForm.lastName || "",
      },
      clinics: [
        {
          id: clinicForm.id,
          BSNR: clinicForm.BSNR || clinicForm.id || undefined,
          contactInfo: {
            businessName: clinicForm.businessName,
            phone1: clinicForm.phone1,
            fax: clinicForm.fax,
            addressLine1: clinicForm.addressLine1,
            addressLine2: clinicForm.addressLine2,
            city: clinicForm.city,
            zip: clinicForm.zip,
            email: clinicForm.email,
          },
          notes: clinicForm.notes,
          methodForDrReport: (clinicForm.methodForDrReport as ReportMethod) || null,
          methodForRxOrder: (clinicForm.methodForRxOrder as ReportMethod) || null,
        },
      ],
    };

    // Call createDoctorWithClinic to save data
    setLoading(true);
    try {
      const response = await createDoctorWithClinicMutation.mutateAsync(createDoctorClinicDto);
      handleCloseAddEditDialog();
      setClinicForm(initialClinicForm);
      setDoctorForms(initialDoctorForms);
      setNewDoctorForm(null);
      onDataSaved();
      return response;
    } catch (error) {
      console.error("Error saving doctor and clinic:", error);
  setErrorMessage("Beim Speichern von Arzt und Praxis ist ein Fehler aufgetreten. Bitte versuche es erneut.");
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  // Cancel function - Reset form to initial state
  const handleCancel = () => {
    setClinicForm(initialClinicForm);
    setDoctorForms(initialDoctorForms);
    setNewDoctorForm(null);
    setIsNewDoctor(false);
    setIsClinicValid(false);
    setIsDoctorValid(false);
    handleCloseAddEditDialog();
  };

  const fetchClinicData = async (clinicId: string) => {
    const clinicData = (await getClinicById(clinicId)) as ClinicForModal;
    if (clinicData) {
      setIsClinicValid(true);

      setClinicForm((prevForm) => ({
        ...prevForm,
        id: clinicData.id || clinicId,
        BSNR: clinicData.BSNR || prevForm.BSNR || "",
        businessName: clinicData.contactInfo?.businessName || "",
        phone1: clinicData.contactInfo?.phone1 || "",
        fax: clinicData.contactInfo?.fax || "",
        addressLine1: clinicData.contactInfo?.addressLine1 || "",
        addressLine2: clinicData.contactInfo?.addressLine2 || "",
        city: clinicData.contactInfo?.city || "",
        zip: clinicData.contactInfo?.zip || "",
        email: clinicData.contactInfo?.email || "",
        notes: clinicData.notes || "",
        methodForDrReport: clinicData.methodForDrReport || "",
        methodForRxOrder: clinicData.methodForRxOrder || "",
      }));
      // Fill in the doctor forms with the doctors from the clinic data
      if (clinicData.doctors && clinicData.doctors.length < 1) {
        setDoctorForms(initialDoctorForms);
      } else if (clinicData.doctors && clinicData.doctors.length > 0) {
        const updatedDoctorForms = clinicData.doctors.map((doctor: any) => ({
          id: doctor.id,
          LANR: doctor.LANR || "",
          salutation: doctor.contactInfo?.salutation || "",
          firstNames: doctor.contactInfo?.firstNames || "",
          lastName: doctor.contactInfo?.lastName || "",
        }));
        setDoctorForms(updatedDoctorForms); // Update doctor forms based on fetched data
      }
    } else {
      // Clinic not found: this is the "create new" flow. Do NOT wipe the
      // user's already-entered values — only mark the clinic as not-yet-existing.
      setIsClinicValid(false);
    }
  };

  useEffect(() => {
    const fetchAndSetClinic = async () => {
      if (clinicId && clinicId.trim() !== "") {
        setLoading(true);
        try {
          await fetchClinicData(clinicId);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAndSetClinic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  // Handle input changes for both clinic and doctor info
  const handleInputChange = async (
    e: React.ChangeEvent<HTMLElement> | SelectChangeEvent<any>,
    formType: "clinic" | "doctor" | "doctorToEdit" | "clinicToEdit",
    index?: number
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    // console.log("Input change:", name, value, formType, index);
    if (formType === "clinic") {
      if (name === "id") {
        setClinicForm({
          ...clinicForm,
          id: value,
          BSNR: value,
        });
        if (value.trim() !== "" && value.trim().length === 9) {
          fetchClinicData(value);
        }
      } else {
        setClinicForm({
          ...clinicForm,
          [name]: value,
        });
      }
    } else if (formType === "doctor" && index === undefined) {
      // Update newDoctorForm state with the new input value
      setNewDoctorForm((prev) => ({
        ...((prev as any) || { id: "", LANR: "", salutation: "", firstNames: "", lastName: "" }),
        [name]: value,
      }));
      // Auto-complete doctor data only when a full 9-digit LANR is entered —
      if (name === "id") {
      if (value.trim().length === 9) {
        const doctorData = await getDoctorById(value);
        if (doctorData) {
          setIsDoctorValid(true);
          setNewDoctorForm({
            id: doctorData.LANR || value,
            LANR: doctorData.LANR || "",
            salutation: doctorData.contactInfo?.salutation || "",
            firstNames: doctorData.contactInfo?.firstNames || "",
            lastName: doctorData.contactInfo?.lastName || "",
          });
        } else {
          setIsDoctorValid(false);
          setNewDoctorForm((prev) => ({
            id: (prev && prev.id) || "",
            LANR: "",
            salutation: "",
            firstNames: "",
            lastName: "",
          }));
          }
        } else {
          setIsDoctorValid(false);
        }
      }
    } else if (formType === "doctorToEdit") {
      if (["id", "salutation", "firstNames", "lastName"].includes(name)) {
        setDoctorToEdit((prevDoctor: DoctorToUpdate) => ({
          ...prevDoctor,
          [name]: value,
        }));
      }
    } else if (formType === "clinicToEdit") {
      if (
        [
          "id",
          "businessName",
          "phone1",
          "fax",
          "addressLine1",
          "addressLine2",
          "city",
          "zip",
          "email",
          "notes",
          "methodForDrReport",
          "methodForRxOrder",
        ].includes(name)
      ) {
        setClinicToEdit((prevClinic: ClinicToUpdate) => ({
          ...prevClinic,
          [name]: value,
        }));
      }
    }
  };

  // Toggle new doctor form visibility
  const handleAddNewDoctor = () => {
    setIsNewDoctor(true);
  };

  const handleDeleteDoctor = (doctor: any, action: "delete" | "removeFromClinic") => {
    setDoctorToDelete(doctor);
    setDeleteAction(action);
    setOpenDeleteDialog(true);
  };

  const handleEditDoctor = (doctor: any) => {
    setDoctorToEdit(doctor);
    setOpenEditDialog(true);
    setEditedDoctorId(doctor.id);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDoctorToDelete(null);
  };

  const handleConfirmDelete = async () => {
    setOpenDeleteDialog(false);
    try {
      await removeDoctorMutation.mutateAsync(doctorToDelete.id);
      if (clinicId) {
        await fetchClinicData(clinicId);
      } else {
        setDoctorForms((prev) => prev.filter((doc) => doc.id !== doctorToDelete.id));
      }
    } catch (error) {
      console.error("Error deleting doctor:", error);
    }
  };

  const handleRemoveFromClinic = async () => {
    setOpenDeleteDialog(false);
    setLoading(true);
    try {
      await removeDoctorFromClinic(doctorToDelete.id, clinicForm.id);
      if (clinicId) {
        await fetchClinicData(clinicId);
      } else {
        setDoctorForms((prev) => prev.filter((doc) => doc.id !== doctorToDelete.id));
      }
    } catch (error) {
      console.error("Error removing doctor from clinic:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDoctorFunc = async () => {
    setOpenEditDialog(false);
    try {
      const payload = {
        id: editedDoctorId,
        contactInfo: {
          salutation: doctorToEdit.salutation || "",
          firstNames: doctorToEdit.firstNames || "",
          lastName: doctorToEdit.lastName || "",
        },
      };
      await updateDoctorMutation.mutateAsync({ id: editedDoctorId, dto: payload });
      setDoctorForms((prev) =>
        prev.map((doc) => (doc.id === doctorToEdit.id ? doctorToEdit : doc))
      );
    } catch (error) {
      console.error("Error removing doctor from clinic:", error);
    }
  };

  const handleEditClinic = (clinicData: any) => {
    setClinicToEdit(clinicData);
    setOpenEditClinicDialog(true);
  };

  const handleUpdateClinicFunc = async () => {
    setOpenEditClinicDialog(false);
    setLoading(true);
    try {
      const payload = {
        id: clinicToEdit.id,
        contactInfo: {
          businessName: clinicToEdit.businessName || "",
          phone1: clinicToEdit.phone1 || "",
          fax: clinicToEdit.fax || "",
          addressLine1: clinicToEdit.addressLine1 || "",
          addressLine2: clinicToEdit.addressLine2 || "",
          city: clinicToEdit.city || "",
          zip: clinicToEdit.zip || "",
          email: clinicToEdit.email || "",
        },
        notes: clinicToEdit.notes || "",
        methodForDrReport: clinicToEdit.methodForDrReport ? clinicToEdit.methodForDrReport : null,
        methodForRxOrder: clinicToEdit.methodForRxOrder ? clinicToEdit.methodForRxOrder : null,
      };
      const updatedDoctorResult = await updateClinic(clinicToEdit.id, payload);
      setClinicForm((prev) => ({
        id: payload.id,
        BSNR: prev.BSNR,
        businessName: payload.contactInfo.businessName,
        phone1: payload.contactInfo.phone1,
        fax: payload.contactInfo.fax,
        addressLine1: payload.contactInfo.addressLine1,
        addressLine2: payload.contactInfo.addressLine2,
        city: payload.contactInfo.city,
        zip: payload.contactInfo.zip,
        email: payload.contactInfo.email,
        notes: payload.notes,
        methodForDrReport: payload.methodForDrReport as ReportMethod,
        methodForRxOrder: payload.methodForRxOrder as ReportMethod,
      }));
    } catch (error) {
      console.error("Error removing doctor from clinic:", error);
    } finally {
      setLoading(false);
      onDataSaved();
    }
  };

  const handleDeleteClinic = async (clinicData: any) => {
    setClinicToDelete(clinicData);
    setOpenDeleteClinicDialog(true);
  };

  const handleDeleteClinicFunc = async () => {
    setOpenDeleteClinicDialog(false);
    setLoading(true);
    try {
      const deletedDoctorResult = await removeClinic(clinicToDelete.id);
      setClinicForm(initialClinicForm);
      setDoctorForms(initialDoctorForms);
      setIsClinicValid(false);
      setIsDoctorValid(false);
    } catch (error) {
      console.error("Error removing clinic:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={openAddEditDialog} onClose={handleCancel} maxWidth="md">
      <DialogTitle>Arzt und Praxis verwalten</DialogTitle>
      <DialogContent>
        {(loading || removeDoctorMutation.isPending || updateDoctorMutation.isPending || createDoctorWithClinicMutation.isPending) && (
            <LoadingSpinner />
          )}

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Grid item xs={12}>
              <h3 style={{ marginTop: 10, marginBottom: 10 }}>Praxis Info</h3>
              {isClinicValid && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    style={{
                      paddingLeft: 10,
                      marginTop: 10,
                      marginRight: 10,
                      height: 30,
                      textTransform: "none",
                    }}
                    size="small"
                    color="primary"
                    onClick={() => handleEditClinic(clinicForm)}
                  >
                    Bearbeiten
                  </Button>
                  <IconButton
                    size="small"
                    style={{ marginTop: 10 }}
                    aria-label="Praxis-Aktionen"
                    onClick={(e) => setClinicKebabAnchorEl(e.currentTarget)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
              <Menu
                anchorEl={clinicKebabAnchorEl}
                open={Boolean(clinicKebabAnchorEl)}
                onClose={() => setClinicKebabAnchorEl(null)}
              >
                <MenuItem
                  onClick={() => {
                    setClinicKebabAnchorEl(null);
                    setMergeClinicOpen(true);
                  }}
                >
                  Zusammenführen
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setClinicKebabAnchorEl(null);
                    handleDeleteClinic(clinicForm);
                  }}
                >
                  Löschen
                </MenuItem>
              </Menu>
              {isClinicValid ? (
                <>
                  <ClinicInfoDisplay clinic={clinicForm} />
                </>
              ) : (
                <ClinicInfoEditForm
                  clinicForm={clinicForm}
                  onInputChange={(e) => handleInputChange(e, "clinic")}
                  ReportMethod={ReportMethod}
                />
              )}
            </Grid>
          </Grid>

          <Grid item xs={6}>
            {/* Show list of doctors if clinic ID is valid */}
            {isClinicValid && doctorForms.length > 0 && (
              <div>
                <h3 style={{ marginTop: "20px", marginBottom: 0 }}>Vorhandene Ärzte</h3>
                {doctorForms.map((doctor, index) => {
                  if (
                    doctor.id === "" &&
                    doctor.salutation === "" &&
                    doctor.firstNames === "" &&
                    doctor.lastName === ""
                  ) {
                    return null;
                  }
                  return (
                    <div key={doctor.id || index} style={{ marginBottom: "20px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ marginTop: 10 }}>Arzt {index + 1}</h4>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Button
                            style={{
                              paddingLeft: 10,
                              marginTop: 10,
                              marginRight: 10,
                              height: 30,
                              textTransform: "none",
                            }}
                            size="small"
                            color="primary"
                            onClick={() => handleEditDoctor(doctor)}
                          >
                            Bearbeiten
                          </Button>
                          <IconButton
                            size="small"
                            style={{ marginTop: 10 }}
                            onClick={(e) => {
                              setKebabAnchorEl(e.currentTarget);
                              setKebabDoctor(doctor);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body1">
                            <span style={{ fontWeight: "bold" }}>LANR:</span> {doctor.LANR || "–"}
                          </Typography>{" "}
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="body1">
                            <span style={{ fontWeight: "bold" }}>Anrede:</span>{" "}
                            {doctor.salutation || "N/A"}
                          </Typography>{" "}
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="body1">
                            <span style={{ fontWeight: "bold" }}>Vorname(n):</span>{" "}
                            {doctor.firstNames || "N/A"}
                          </Typography>{" "}
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="body1">
                            <span style={{ fontWeight: "bold" }}>Nachname:</span>{" "}
                            {doctor.lastName || "N/A"}
                          </Typography>{" "}
                        </Grid>
                      </Grid>
                    </div>
                  );
                })}
              </div>
            )}

            <Button
              onClick={handleAddNewDoctor}
              color="primary"
              variant="contained"
              style={{ marginTop: "20px" }}
            >
              Neuen Arzt anlegen
            </Button>

            {isNewDoctor && (
              <div style={{ marginTop: "20px" }}>
                <h4 style={{ marginTop: 0, marginBottom: 0 }}>
                  Neuen Arzt anlegen {isDoctorValid && "(Arzt existiert)"}
                </h4>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="LANR"
                      name="id"
                      value={newDoctorForm?.id || ""}
                      onChange={(e) => handleInputChange(e, "doctor")}
                      style={{ marginBottom: 0 }}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        style: {
                          height: "50px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Anrede"
                      name="salutation"
                      value={newDoctorForm?.salutation || ""}
                      onChange={(e) => handleInputChange(e, "doctor")}
                      style={{ marginTop: 0, marginBottom: 0 }}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        style: {
                          height: "50px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Vorname(n)"
                      name="firstNames"
                      value={newDoctorForm?.firstNames || ""}
                      style={{ marginTop: 0, marginBottom: 0 }}
                      onChange={(e) => handleInputChange(e, "doctor")}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        style: {
                          height: "50px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Nachname"
                      name="lastName"
                      value={newDoctorForm?.lastName || ""}
                      style={{ marginTop: 0, marginBottom: 0 }}
                      onChange={(e) => handleInputChange(e, "doctor")}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        style: {
                          height: "50px",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
            )}
          </Grid>
        </Grid>

        {/* Tickets & Dr Reports sections (only when viewing existing clinic) */}
        {isClinicValid && clinicId && (
          <Box sx={{ mt: 3, borderTop: "1px solid rgba(0,0,0,0.12)", pt: 2 }}>
            <ClinicTicketsSection clinicId={clinicId} />
            <ClinicDrReportsSection clinicBsnr={clinicForm.BSNR || clinicForm.id || clinicId} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!isNewDoctor ? (
          <Button variant="text" onClick={handleCancel}>
            Schließen
          </Button>
        ) : (
          <>
            <Button variant="text" onClick={handleCancel}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={
                !newDoctorForm ||
                !newDoctorForm.id ||
                !newDoctorForm.lastName
              }
            >
              Speichern
            </Button>
          </>
        )}
      </DialogActions>
      <DeleteDoctorDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={deleteAction === "delete" ? handleConfirmDelete : handleRemoveFromClinic}
        doctorName={doctorToDelete ? `${doctorToDelete.firstNames || ""} ${doctorToDelete.lastName || ""}`.trim() : ""}
        action={deleteAction}
      />
      <ErrorDialog
        open={openErrorDialog}
        onClose={() => setOpenErrorDialog(false)}
        errorMessage={errorMessage}
      />
      <EditDoctorDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        doctorToEdit={doctorToEdit}
        onInputChange={(e) => handleInputChange(e, "doctorToEdit")}
        onSave={handleUpdateDoctorFunc}
      />
      <EditClinicDialog
        open={openEditClinicDialog}
        onClose={() => setOpenEditClinicDialog(false)}
        clinicToEdit={clinicToEdit}
        onInputChange={(e) => handleInputChange(e, "clinicToEdit")}
        onSave={handleUpdateClinicFunc}
        ReportMethod={ReportMethod}
      />
      <DeleteClinicDialog
        open={openDeleteClinicDialog}
        onClose={() => setOpenDeleteClinicDialog(false)}
        onDelete={handleDeleteClinicFunc}
      />
      <Menu
        anchorEl={kebabAnchorEl}
        open={Boolean(kebabAnchorEl)}
        onClose={() => {
          setKebabAnchorEl(null);
          setKebabDoctor(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setKebabAnchorEl(null);
            if (kebabDoctor?.id) {
              setMergeDoctorId(kebabDoctor.id);
              setMergeDoctorOpen(true);
            }
          }}
        >
          Zusammenführen
        </MenuItem>
        <MenuItem
          onClick={() => {
            setKebabAnchorEl(null);
            if (kebabDoctor) {
              handleDeleteDoctor(kebabDoctor, "removeFromClinic");
            }
          }}
        >
          von Klinik entfernen
        </MenuItem>
        <MenuItem
          onClick={() => {
            setKebabAnchorEl(null);
            if (kebabDoctor) {
              handleDeleteDoctor(kebabDoctor, "delete");
            }
          }}
        >
          Löschen
        </MenuItem>
      </Menu>
      <MergeEntityDialog
        open={mergeDoctorOpen}
        onClose={() => setMergeDoctorOpen(false)}
        entityType="doctor"
        targetId={mergeDoctorId ?? ""}
        onMerged={() => {
          setMergeDoctorOpen(false);
          setMergeDoctorId(null);
          if (clinicId) fetchClinicData(clinicId);
          onDataSaved();
        }}
      />
      <MergeEntityDialog
        open={mergeClinicOpen}
        onClose={() => setMergeClinicOpen(false)}
        entityType="clinic"
        targetId={clinicForm?.id || clinicId || ""}
        onMerged={() => {
          setMergeClinicOpen(false);
          if (clinicId) fetchClinicData(clinicId);
          onDataSaved();
        }}
      />
    </Dialog>
  );
};
export default DoctorClinicDialog;
